import classNames from 'classnames';
import React, { useRef } from 'react';
import { Popover, Tooltip } from '@meitu/saas-ui';
import { type HighlightMaterial } from '@meitu/saas-editor-core/es/framework/services/text-highlight';
import { imageConverter } from '@meitu/saas-editor-core/es/utils/file-kit';
import { TextWritingType, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { EditorContext, useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { useEffectiveShapeExtension, useHighlight } from '../../hooks';
import { HighlightPanel } from '../../panels';
import { type WidgetCommonProps } from '../../types';
import { getHighlightThumbnailURL } from '../../utils';
import './style.less';

type Props = React.ComponentProps<'div'> & WidgetCommonProps & { highlights: HighlightMaterial[] };

export const HighlightEntry: React.FC<Props> = ({ theme, isIntl, style, highlights, ...restProps }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();

    const popoverRef = useRef<any>(null);
    const popoverWidgetRef = useGlobalWidget(editor, false);

    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        return { writingMode: layerData.writingMode };
    });

    const writingMode = props ? props.writingMode : TextWritingType.HORIZONTAL_TB;
    const shapeExtension = useEffectiveShapeExtension(restProps.layerId);

    const disableTip = (() => {
        if (shapeExtension) {
            return $t((v) => v.textMarkSelector.disabledByTransform);
        }

        if (writingMode !== TextWritingType.HORIZONTAL_TB) {
            return $t((v) => v.textMarkSelector.disabledByWritingMode);
        }
        return '';
    })();

    const { hasNoHighlight, hasMultipleHighlights, highlight, highlightsInTextContents } = useHighlight(editor);

    const targetHighlightValue = (() => {
        if (highlightsInTextContents.length === 1) return highlightsInTextContents[0];
        return highlight;
    })();

    const defaultHighlight = highlights[0];
    const defaultHighlightThumbnailURL = defaultHighlight
        ? getHighlightThumbnailURL(defaultHighlight, theme, isIntl)
        : '';

    const targetHighlight = targetHighlightValue
        ? highlights.find((item) => String(item.id) === String(targetHighlightValue.materialId))
        : null;

    const thumbnailURL = targetHighlight ? getHighlightThumbnailURL(targetHighlight, theme, isIntl) : '';

    const rootCls = classNames('modal-panel-entry', { 'modal-panel-entry--disabled': disableTip !== '' });

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disableTip) return e.stopPropagation();
    };

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;
    };

    return (
        <Popover
            trigger="click"
            placement="left"
            ref={popoverRef}
            content={
                <EditorContext.Provider value={editor}>
                    <HighlightPanel
                        {...restProps}
                        theme={theme}
                        isIntl={isIntl}
                        highlights={highlights}
                        onClose={() => {
                            // 模拟弹窗外点击关闭弹窗
                            popoverRef.current?.onClick?.();
                        }}
                    />
                </EditorContext.Provider>
            }
            overlayClassName="text-layer-panel-popover"
            destroyTooltipOnHide
            onOpenChange={handleOpenChange}
        >
            <Tooltip title={disableTip}>
                <div className={rootCls} onClickCapture={handleClick} style={style}>
                    <div className="modal-panel-entry__thumbnail">
                        {hasNoHighlight ? (
                            <div
                                className="sub-panel-entry__cover"
                                style={{
                                    backgroundImage: `url(${defaultHighlightThumbnailURL ? imageConverter(defaultHighlightThumbnailURL, 116) : ''})`,
                                }}
                            />
                        ) : null}
                        {hasMultipleHighlights ? <div className="modal-panel-entry__dots">···</div> : null}
                        {targetHighlightValue && thumbnailURL && !hasMultipleHighlights ? (
                            <div
                                className="sub-panel-entry__cover"
                                style={{
                                    backgroundImage: `url(${imageConverter(thumbnailURL, 116)})`,
                                }}
                            />
                        ) : null}
                    </div>
                    <div className="modal-panel-entry__title">{$t((v) => v.textMarkSelector.mark)}</div>
                </div>
            </Tooltip>
        </Popover>
    );
};
